import { DiscordSDK } from '@discord/embedded-app-sdk'
import { FormEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { CSSProperties, ReactNode } from 'react'
import { activityRequest, activityWebSocketUrl, ActivityApiError } from './api'
import {
  applyPermissionSnapshot,
  createPermissionSync,
} from '../../dashboard/src/lib/permissionSync'
import { BrandWave, Icon } from './Icons'
import brandLogo from '../../icons/bestmusicboat.png'
import type {
  GuildPlayerContext,
  PlayerControlAction,
  PlayerPermissions,
  PlayerSearchRequest,
  PlayerSearchResponse,
  PlayerState,
  PlayerWebSocketMessage,
  SearchTrack,
} from '../../src/web-player/contracts.js'

type RealtimeStatus = 'idle' | 'connecting' | 'connected' | 'reconnecting' | 'error'

interface ActivityAuthorization {
  accessToken: string
  sessionToken: string
  expiresAt: number
}

interface ActivityInitialization {
  guildId: string
  sessionToken: string
}

let activityInitialization: Promise<ActivityInitialization> | null = null

function setupErrorMessage(step: string, reason: unknown): string {
  if (reason instanceof Error) return `${step}: ${reason.message}`
  if (typeof reason === 'string' && reason.trim()) return `${step}: ${reason}`
  if (reason && typeof reason === 'object') {
    const details = reason as Record<string, unknown>
    const message = typeof details.message === 'string' ? details.message.trim() : ''
    const code =
      typeof details.code === 'string' || typeof details.code === 'number'
        ? String(details.code)
        : ''
    if (message || code) {
      return `${step}: ${message || 'Discord rejected the request'}${code ? ` (code ${code})` : ''}`
    }
  }
  return `${step}: Discord Activity setup failed.`
}

function initializeDiscordActivity(): Promise<ActivityInitialization> {
  if (activityInitialization) return activityInitialization

  let setupStep = 'Initializing the Discord SDK'
  const initialize = async (): Promise<ActivityInitialization> => {
    const clientId = import.meta.env.VITE_DISCORD_CLIENT_ID?.trim()
    if (!clientId) throw new Error('VITE_DISCORD_CLIENT_ID is not configured for this Activity.')

    const sdk = new DiscordSDK(clientId)
    setupStep = 'Waiting for the Discord SDK'
    await sdk.ready()
    if (!sdk.guildId) {
      throw new Error('Open Best Music Boat from a server voice channel, not a DM.')
    }

    setupStep = 'Authorizing with Discord'
    const { code } = await sdk.commands.authorize({
      client_id: clientId,
      response_type: 'code',
      state: crypto.randomUUID(),
      prompt: 'none',
      scope: ['identify', 'guilds'],
    })

    setupStep = 'Exchanging the Discord authorization code'
    const authorization = await activityRequest<ActivityAuthorization>(
      '/auth/exchange',
      undefined,
      {
        method: 'POST',
        body: JSON.stringify({ code }),
      }
    )

    setupStep = 'Authenticating the Discord Activity'
    const authenticated = await sdk.commands.authenticate({
      access_token: authorization.accessToken,
    })
    if (!authenticated) throw new Error('Discord returned an empty authentication response.')

    return { guildId: sdk.guildId, sessionToken: authorization.sessionToken }
  }

  activityInitialization = initialize().catch((reason) => {
    activityInitialization = null
    throw new Error(setupErrorMessage(setupStep, reason))
  })
  return activityInitialization
}

function formatDuration(value: number, stream = false): string {
  if (stream) return 'LIVE'
  const seconds = Math.max(0, Math.floor(value / 1000))
  const hours = Math.floor(seconds / 3600)
  const minutes = Math.floor((seconds % 3600) / 60)
  const remainder = String(seconds % 60).padStart(2, '0')
  return hours
    ? `${hours}:${String(minutes).padStart(2, '0')}:${remainder}`
    : `${minutes}:${remainder}`
}

function messageForPermission(permissions: PlayerPermissions | null): string | null {
  if (!permissions || permissions.canControl || permissions.canStart) return null
  const messages: Record<string, string> = {
    discord_unavailable: 'Discord is reconnecting. Voice permissions will refresh automatically.',
    not_in_voice: 'Join a voice channel to control Best Music Boat.',
    different_voice: 'Join the same voice channel as Best Music Boat.',
    dj_required: 'Only DJs, managers, and administrators can control this player.',
    bot_missing_voice_permissions:
      'The bot needs permission to join and speak in this voice channel.',
    lavalink_unavailable: 'The music service is temporarily unavailable.',
  }
  return (
    messages[permissions.reason] ?? 'You can view this player, but cannot control it right now.'
  )
}

function nextLoop(mode: PlayerState['loop']): PlayerState['loop'] {
  return mode === 'none' ? 'song' : mode === 'song' ? 'queue' : 'none'
}

function updateIfNewer(
  previous: PlayerState | null,
  next: PlayerState,
  authoritative = false
): PlayerState {
  if (!previous) return next
  if (previous.guildId !== next.guildId) return previous
  if (previous.streamId !== next.streamId) return authoritative ? next : previous
  if (next.revision > previous.revision) return next
  return previous
}

export function App() {
  const [phase, setPhase] = useState<'loading' | 'ready' | 'error'>('loading')
  const [error, setError] = useState<string | null>(null)
  const [token, setToken] = useState<string | null>(null)
  const [guildId, setGuildId] = useState<string | null>(null)
  const [player, setPlayer] = useState<PlayerState | null>(null)
  const [permissions, setPermissions] = useState<PlayerPermissions | null>(null)
  const [realtime, setRealtime] = useState<RealtimeStatus>('idle')
  const [busy, setBusy] = useState<PlayerControlAction['type'] | null>(null)
  const permissionSync = useRef(createPermissionSync())
  const commitPermissions = useCallback((incoming: PlayerPermissions) => {
    const next = applyPermissionSnapshot(permissionSync.current, incoming)
    if (next !== permissionSync.current) {
      permissionSync.current = next
      setPermissions(next.value)
    }
  }, [])
  const playerRef = useRef<PlayerState | null>(null)

  const commitPlayer = useCallback(
    (updater: PlayerState | ((current: PlayerState | null) => PlayerState | null)) => {
      setPlayer((current) => {
        const next = typeof updater === 'function' ? updater(current) : updater
        playerRef.current = next
        return next
      })
    },
    []
  )

  useEffect(() => {
    let cancelled = false
    const initialize = async () => {
      const loadingStartedAt = performance.now()
      const initialized = await initializeDiscordActivity()
      const remainingSplashTime = Math.max(0, 1_400 - (performance.now() - loadingStartedAt))
      if (remainingSplashTime) {
        await new Promise((resolve) => window.setTimeout(resolve, remainingSplashTime))
      }
      if (!cancelled) {
        setToken(initialized.sessionToken)
        setGuildId(initialized.guildId)
        setPhase('ready')
      }
    }
    void initialize().catch((reason) => {
      if (!cancelled) {
        setError(reason instanceof Error ? reason.message : 'Discord Activity setup failed.')
        setPhase('error')
      }
    })
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    if (!token || !guildId) return
    let closed = false
    let socket: WebSocket | null = null
    let retryTimer: number | null = null
    let heartbeat: number | null = null
    let attempts = 0
    let generation = 0
    let pollInFlight = false
    permissionSync.current = createPermissionSync()

    const connect = async () => {
      if (closed) return
      const currentGeneration = ++generation
      setRealtime(attempts ? 'reconnecting' : 'connecting')
      try {
        const context = await activityRequest<GuildPlayerContext>(
          `/guilds/${guildId}/context?offset=0&limit=100`,
          token
        )
        if (closed || currentGeneration !== generation) return
        commitPlayer((current) => updateIfNewer(current, context.state, true))
        commitPermissions(context.permissions)
        const ticket = await activityRequest<{ ticket: string }>(
          `/player/${guildId}/socket-ticket`,
          token,
          { method: 'POST', body: '{}' }
        )
        if (closed || currentGeneration !== generation) return
        socket = new WebSocket(activityWebSocketUrl(ticket.ticket))
        let lastMessageAt = Date.now()
        socket.onopen = () => {
          if (closed || currentGeneration !== generation) return
          setRealtime('connected')
          heartbeat = window.setInterval(() => {
            if (Date.now() - lastMessageAt > 60_000) {
              socket?.close(4000, 'heartbeat_timeout')
              return
            }
            if (socket?.readyState === WebSocket.OPEN) socket.send('ping')
          }, 25_000)
        }
        socket.onmessage = (event) => {
          if (closed || currentGeneration !== generation) return
          lastMessageAt = Date.now()
          try {
            const message = JSON.parse(String(event.data)) as PlayerWebSocketMessage
            if (message.type === 'ready' && message.guildId === guildId) {
              commitPlayer((current) => updateIfNewer(current, message.state, true))
              commitPermissions(message.permissions)
              setRealtime('connected')
              attempts = 0
            } else if (message.type === 'permissions' && message.guildId === guildId) {
              commitPermissions(message.permissions)
            } else if (message.type === 'error') {
              setError(message.error.message)
              setRealtime('error')
            } else if (message.type === 'event' && message.event.guildId === guildId) {
              const update = message.event
              if (update.type === 'snapshot') {
                commitPlayer((current) =>
                  updateIfNewer(current, update.state, update.authoritative === true)
                )
              } else if (update.type === 'position') {
                commitPlayer((current) => {
                  if (
                    !current ||
                    current.streamId !== update.streamId ||
                    current.playbackId !== update.playbackId ||
                    current.revision >= update.sequence
                  ) {
                    return current
                  }
                  return {
                    ...current,
                    revision: update.sequence,
                    position: update.position,
                    paused: update.paused,
                    playing: update.playing,
                    serverTime: update.serverTime,
                  }
                })
              } else if (update.type === 'playerDestroyed') {
                commitPlayer((current) =>
                  current &&
                  current.streamId === update.streamId &&
                  current.playbackId === update.playbackId &&
                  current.revision < update.sequence
                    ? {
                        ...current,
                        revision: update.sequence,
                        active: false,
                        playing: false,
                        paused: false,
                        current: null,
                        position: 0,
                        playbackId: null,
                        queue: { ...current.queue, items: [], total: 0, hasMore: false },
                        connection: 'idle',
                      }
                    : current
                )
              } else if (update.type === 'clusterStatus') {
                setRealtime(update.status === 'available' ? 'connecting' : 'reconnecting')
              }
            }
          } catch {
            setError('The real-time player sent an invalid update.')
          }
        }
        socket.onclose = (event) => {
          if (closed || currentGeneration !== generation || event.code === 1000) return
          if (heartbeat) window.clearInterval(heartbeat)
          if (event.code === 1008) {
            setRealtime('error')
            setError(
              event.reason === 'SESSION_EXPIRED'
                ? 'Your Activity session expired. Reopen the Activity.'
                : 'Real-time access was denied.'
            )
            return
          }
          setRealtime('reconnecting')
          attempts += 1
          if (attempts > 8) {
            setRealtime('error')
            setError('The real-time connection could not recover. Reopen the Activity to retry.')
            return
          }
          retryTimer = window.setTimeout(
            connect,
            Math.min(15_000, 1_000 * 2 ** Math.min(4, attempts))
          )
        }
      } catch (reason) {
        if (closed || currentGeneration !== generation) return
        const message =
          reason instanceof Error ? reason.message : 'Unable to connect to the player.'
        setError(message)
        setRealtime('error')
        if (!(reason instanceof ActivityApiError && !reason.details.retryable)) {
          attempts += 1
          if (attempts > 8) return
          retryTimer = window.setTimeout(
            connect,
            Math.min(15_000, 1_000 * 2 ** Math.min(4, attempts))
          )
        }
      }
    }
    void connect()
    const poll = window.setInterval(async () => {
      if (closed || pollInFlight) return
      pollInFlight = true
      const requestedStream = playerRef.current?.streamId
      try {
        const context = await activityRequest<GuildPlayerContext>(
          `/guilds/${guildId}/context?offset=0&limit=100`,
          token
        )
        if (closed) return
        commitPlayer((current) =>
          current &&
          current.streamId !== context.state.streamId &&
          current.streamId !== requestedStream
            ? current
            : updateIfNewer(current, context.state, true)
        )
        commitPermissions(context.permissions)
      } catch {
        /* The socket and next reconciliation handle transient failures. */
      } finally {
        pollInFlight = false
      }
    }, 2_500)
    return () => {
      closed = true
      window.clearInterval(poll)
      if (retryTimer) window.clearTimeout(retryTimer)
      if (heartbeat) window.clearInterval(heartbeat)
      socket?.close(1000, 'activity_closed')
    }
  }, [commitPermissions, commitPlayer, guildId, token])

  const performAction = useCallback(
    async (action: PlayerControlAction) => {
      if (!token || !guildId || busy) return false
      setBusy(action.type)
      setError(null)
      try {
        const next = await activityRequest<PlayerState>(`/player/${guildId}/actions`, token, {
          method: 'POST',
          body: JSON.stringify(action),
        })
        commitPlayer((current) => updateIfNewer(current, next))
        return true
      } catch (reason) {
        setError(
          reason instanceof Error ? reason.message : 'The player could not complete that action.'
        )
        return false
      } finally {
        setBusy(null)
      }
    },
    [busy, commitPlayer, guildId, token]
  )

  const loadMoreQueue = useCallback(async () => {
    const current = playerRef.current
    if (!token || !guildId || !current?.queue.hasMore) return
    try {
      const incoming = await activityRequest<PlayerState>(
        `/player/${guildId}?offset=${current.queue.items.length}&limit=100`,
        token
      )
      commitPlayer((previous) => {
        if (
          !previous ||
          previous.guildId !== incoming.guildId ||
          previous.streamId !== incoming.streamId ||
          previous.revision >= incoming.revision
        ) {
          return previous
        }
        if (previous.queue.version !== incoming.queue.version)
          return updateIfNewer(previous, incoming)
        return {
          ...incoming,
          queue: {
            ...incoming.queue,
            items: [...previous.queue.items, ...incoming.queue.items],
            offset: 0,
            limit: previous.queue.items.length + incoming.queue.items.length,
          },
        }
      })
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'Unable to load more of the queue.')
    }
  }, [commitPlayer, guildId, token])

  const permissionNotice = useMemo(() => messageForPermission(permissions), [permissions])

  if (phase === 'loading') return <LoadingScreen />
  if (phase === 'error') return <ErrorScreen message={error ?? 'Unable to start the Activity.'} />
  return (
    <main className="activity-shell">
      <div className="activity-body">
        <nav className="side-rail" aria-label="Activity sections">
          <span className="rail-brand" title="Best Music Boat" aria-label="Best Music Boat">
            <img src={brandLogo} alt="" />
          </span>
          <a className="active" href="#home" aria-label="Home" title="Home">
            <Icon name="home" />
          </a>
          <a href="#search" aria-label="Search" title="Search">
            <Icon name="search" />
          </a>
          <a href="#queue" aria-label="Queue" title="Queue">
            <Icon name="queue" />
          </a>
          <a href="#smart-tools" aria-label="Smart tools" title="Smart tools">
            <Icon name="autoplay" />
          </a>
          <span className="rail-spacer" />
          <span className={`rail-connection ${realtime}`} title={`Player: ${realtime}`}>
            <Icon name="status" size={19} />
          </span>
        </nav>
        <div className="content-scroll">
          {error ? (
            <div className="notice error" role="alert">
              <span>{error}</span>
              <button onClick={() => setError(null)} aria-label="Dismiss error">
                ×
              </button>
            </div>
          ) : null}
          {permissionNotice ? <div className="notice permission">{permissionNotice}</div> : null}
          <HomeOverview player={player} realtime={realtime} />
          <PremiumTools
            player={player}
            permissions={permissions}
            token={token}
            guildId={guildId}
            busy={busy}
            onAction={performAction}
            onError={setError}
          />
          <Search
            canStart={Boolean(permissions?.canStart)}
            premium={Boolean(permissions?.premium)}
            token={token}
            guildId={guildId}
            busy={busy}
            onAction={performAction}
            onError={setError}
          />
          <Queue player={player} onLoadMore={loadMoreQueue} />
        </div>
      </div>
      <NowPlaying
        player={player}
        canControl={Boolean(permissions?.canControl)}
        busy={busy}
        onAction={performAction}
      />
    </main>
  )
}

function HomeOverview({
  player,
  realtime,
}: {
  player: PlayerState | null
  realtime: RealtimeStatus
}) {
  const track = player?.current ?? null
  return (
    <section className="home-overview" id="home">
      <div className="home-copy">
        <div className="hero-brand">
          <BrandWave compact />
          <b>Best Music Boat</b>
          <span className={`live-status ${realtime}`}>
            <i />
            {realtime === 'connected'
              ? 'Live'
              : realtime === 'reconnecting'
                ? 'Reconnecting'
                : 'Connecting'}
          </span>
        </div>
        <span className="eyebrow">{track ? 'NOW PLAYING' : 'MUSIC ROOM'}</span>
        <h1>{track?.title ?? 'Your music room is ready.'}</h1>
        <p>
          {track
            ? `${track.author} · Live for everyone in the voice channel.`
            : 'Pick the next track and Best Music Boat keeps every listener perfectly in sync.'}
        </p>
        <a href="#search" className="hero-action">
          <Icon name="search" size={18} />
          Find music
        </a>
      </div>
      <div className="hero-art-wrap">
        <Artwork
          src={track?.thumbnail ?? null}
          title={track?.title ?? 'Best Music Boat'}
          author={track?.author}
          large
        />
        <span className="hero-orbit" />
      </div>
    </section>
  )
}

function NowPlaying({
  player,
  canControl,
  busy,
  onAction,
}: {
  player: PlayerState | null
  canControl: boolean
  busy: string | null
  onAction(action: PlayerControlAction): Promise<boolean>
}) {
  const [position, setPosition] = useState(0)
  const [volume, setVolume] = useState(100)
  const track = player?.current ?? null
  const duration = track?.duration ?? 0
  useEffect(() => setPosition(player?.position ?? 0), [player?.playbackId, player?.position])
  useEffect(() => setVolume(player?.volume ?? 100), [player?.volume])
  useEffect(() => {
    if (!player?.playing || player.paused || !track) return
    const interval = window.setInterval(() => {
      setPosition((current) => Math.min(duration, current + 200))
    }, 200)
    return () => window.clearInterval(interval)
  }, [duration, player?.paused, player?.playing, track])

  const disabled = !canControl || busy !== null
  const seek = () => {
    if (track?.isSeekable && !track.isStream)
      void onAction({ type: 'seek', position: Math.round(position) })
  }
  return (
    <section className="player-dock" aria-label="Now playing">
      {player ? (
        <>
          <div className="dock-track">
            <Artwork
              src={track?.thumbnail ?? null}
              title={track?.title ?? 'Nothing playing'}
              author={track?.author}
            />
            <div>
              <b title={track?.title}>{track?.title ?? 'Nothing is playing'}</b>
              <small>{track?.author ?? 'Search for something to play'}</small>
            </div>
          </div>
          <div className="dock-center">
            <div className="transport" aria-label="Playback controls">
              <Control
                label={`Loop: ${player.loop}`}
                icon={<Icon name="repeat" size={19} />}
                active={player.loop !== 'none'}
                disabled={disabled || !track}
                onClick={() => onAction({ type: 'loop', mode: nextLoop(player.loop) })}
              />
              <Control
                label="Previous"
                icon={<Icon name="previous" size={20} />}
                disabled={disabled || !player.historyAvailable}
                onClick={() => onAction({ type: 'previous' })}
              />
              <Control
                label={player.paused ? 'Resume' : 'Pause'}
                icon={<Icon name={player.paused ? 'play' : 'pause'} size={22} />}
                primary
                disabled={disabled || !track}
                onClick={() => onAction({ type: player.paused ? 'resume' : 'pause' })}
              />
              <Control
                label="Skip"
                icon={<Icon name="next" size={20} />}
                disabled={disabled || !track}
                onClick={() => onAction({ type: 'skip' })}
              />
              <Control
                label="Shuffle queue"
                icon={<Icon name="shuffle" size={19} />}
                disabled={disabled || !player.queue.total}
                onClick={() => onAction({ type: 'shuffle' })}
              />
              <Control
                label="Stop"
                icon={<Icon name="stop" size={16} />}
                danger
                disabled={disabled || !track}
                onClick={() => onAction({ type: 'stop' })}
              />
            </div>
            <div className="progress-row">
              <span>{formatDuration(position)}</span>
              <input
                aria-label="Playback position"
                type="range"
                min="0"
                max={Math.max(1, duration)}
                value={Math.min(position, Math.max(1, duration))}
                style={
                  {
                    '--range-progress': `${duration ? Math.min(100, (position / duration) * 100) : 0}%`,
                  } as CSSProperties
                }
                disabled={disabled || !track?.isSeekable || track.isStream}
                onChange={(event) => setPosition(Number(event.target.value))}
                onPointerUp={seek}
                onKeyUp={(event) => event.key === 'Enter' && seek()}
              />
              <span>{formatDuration(duration, Boolean(track?.isStream))}</span>
            </div>
          </div>
          <div className="volume-row">
            <Icon name="volume" size={19} />
            <input
              aria-label={`Volume ${volume}%`}
              type="range"
              min="1"
              max="100"
              value={volume}
              style={{ '--range-progress': `${volume}%` } as CSSProperties}
              disabled={disabled}
              onChange={(event) => setVolume(Number(event.target.value))}
              onPointerUp={() => void onAction({ type: 'volume', volume })}
              onKeyUp={(event) =>
                event.key === 'Enter' && void onAction({ type: 'volume', volume })
              }
            />
            <b>{volume}%</b>
          </div>
        </>
      ) : (
        <PlayerSkeleton />
      )}
    </section>
  )
}

function Queue({
  player,
  onLoadMore,
}: {
  player: PlayerState | null
  onLoadMore(): Promise<void>
}) {
  const items = player?.queue.items ?? []
  const [loadingMore, setLoadingMore] = useState(false)
  return (
    <aside className="queue card" id="queue">
      <div className="section-heading">
        <span>Queue</span>
        <small>{player?.queue.total ?? 0} up next</small>
      </div>
      {player ? (
        items.length ? (
          <>
            <ol>
              {items.map((track, index) => (
                <li key={track.id}>
                  <span className="queue-index">{index + 1}</span>
                  <Artwork src={track.thumbnail} title={track.title} author={track.author} />
                  <div>
                    <b title={track.title}>{track.title}</b>
                    <small title={track.author}>{track.author}</small>
                  </div>
                  <time>{formatDuration(track.duration, track.isStream)}</time>
                </li>
              ))}
            </ol>
            {player.queue.hasMore ? (
              <button
                className="load-more"
                disabled={loadingMore}
                onClick={() => {
                  setLoadingMore(true)
                  void onLoadMore().finally(() => setLoadingMore(false))
                }}
              >
                {loadingMore ? 'Loading…' : `Show ${player.queue.total - items.length} more`}
              </button>
            ) : null}
          </>
        ) : (
          <div className="queue-empty">
            Your queue is clear.
            <br />
            Find something below to line up next.
          </div>
        )
      ) : (
        <QueueSkeleton />
      )}
    </aside>
  )
}

function Search({
  canStart,
  premium,
  token,
  guildId,
  busy,
  onAction,
  onError,
}: {
  canStart: boolean
  premium: boolean
  token: string | null
  guildId: string | null
  busy: string | null
  onAction(action: PlayerControlAction): Promise<boolean>
  onError(message: string): void
}) {
  const [query, setQuery] = useState('')
  const [source, setSource] = useState<PlayerSearchRequest['source']>('deezer')
  const [results, setResults] = useState<PlayerSearchResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const search = async (event: FormEvent) => {
    event.preventDefault()
    if (!query.trim() || !token || !guildId || loading) return
    setLoading(true)
    try {
      setResults(
        await activityRequest<PlayerSearchResponse>(`/player/${guildId}/search`, token, {
          method: 'POST',
          body: JSON.stringify({ query: query.trim(), source, limit: 20 }),
        })
      )
    } catch (reason) {
      onError(reason instanceof Error ? reason.message : 'Search failed.')
    } finally {
      setLoading(false)
    }
  }
  return (
    <section className="search" id="search">
      <div className="section-heading">
        <span>Find your next favorite</span>
        <small>Tracks, playlists, albums and direct links</small>
      </div>
      <form onSubmit={search}>
        <Icon name="search" size={20} />
        <input
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          maxLength={300}
          placeholder="Search music"
          aria-label="Search music"
        />
        <SourcePicker source={source} onChange={setSource} premium={premium} />
        <button className="accent-button" disabled={!query.trim() || loading || !canStart}>
          {loading ? 'Searching…' : 'Search'}
        </button>
      </form>
      {loading ? (
        <SearchSkeleton />
      ) : results?.tracks.length ? (
        <div className="results">
          {results.tracks.map((track) => (
            <SearchResult
              key={track.trackToken}
              track={track}
              disabled={!canStart || busy !== null}
              onAction={onAction}
            />
          ))}
        </div>
      ) : results ? (
        <div className="queue-empty">No tracks found. Try another title, artist, or URL.</div>
      ) : null}
    </section>
  )
}

function PremiumTools({
  player,
  permissions,
  token,
  guildId,
  busy,
  onAction,
  onError,
}: {
  player: PlayerState | null
  permissions: PlayerPermissions | null
  token: string | null
  guildId: string | null
  busy: string | null
  onAction(action: PlayerControlAction): Promise<boolean>
  onError(message: string): void
}) {
  const premium = Boolean(permissions?.premium)
  const [playlistUrl, setPlaylistUrl] = useState('')
  const [source, setSource] = useState<PlayerSearchRequest['source']>('spotify')
  const [importing, setImporting] = useState(false)
  const [imported, setImported] = useState<string | null>(null)
  const importLimit = premium ? 1_000 : 100

  const importCollection = async (event: FormEvent) => {
    event.preventDefault()
    if (!token || !guildId || !playlistUrl.trim() || importing) return
    setImporting(true)
    setImported(null)
    try {
      const result = await activityRequest<PlayerSearchResponse>(
        `/player/${guildId}/search`,
        token,
        {
          method: 'POST',
          body: JSON.stringify({
            query: playlistUrl.trim(),
            source,
            limit: importLimit,
            purpose: 'playlistImport',
          }),
        }
      )
      const resultType = result.resultType.toUpperCase()
      const looksLikeCollection =
        Boolean(result.playlistName) ||
        resultType.includes('PLAYLIST') ||
        resultType.includes('ALBUM')
      if (!looksLikeCollection || !result.tracks.length) {
        throw new Error('This link does not contain a supported playlist or album.')
      }
      const importedSuccessfully = await onAction({
        type: 'importPlaylist',
        trackTokens: result.tracks.map((track) => track.trackToken),
      })
      if (!importedSuccessfully) return
      setImported(
        `${result.playlistName || 'Collection'} imported · ${result.tracks.length} tracks added`
      )
      setPlaylistUrl('')
    } catch (reason) {
      onError(
        reason instanceof Error ? reason.message : 'The playlist or album could not be imported.'
      )
    } finally {
      setImporting(false)
    }
  }

  const autoplayDisabled =
    busy !== null || !player?.current || !permissions?.canControl || !permissions.canUseAutoplay

  return (
    <section className="smart-tools card" id="smart-tools">
      <div className="autoplay-tool">
        <span className="tool-icon">
          <Icon name="autoplay" size={24} />
        </span>
        <div className="tool-copy">
          <span className="tool-title-row">
            <b>Autoplay</b>
            {!premium ? <PremiumBadge /> : null}
          </span>
          <small>Best Music Boat keeps the music going with matching tracks.</small>
        </div>
        <button
          type="button"
          className={`autoplay-switch ${player?.autoplay ? 'enabled' : ''}`}
          role="switch"
          aria-checked={Boolean(player?.autoplay)}
          aria-label={player?.autoplay ? 'Disable Autoplay' : 'Enable Autoplay'}
          title={!premium ? 'Best Music Boat Premium required' : 'Toggle Autoplay'}
          disabled={autoplayDisabled}
          onClick={() => void onAction({ type: 'autoplay', enabled: !player?.autoplay })}
        >
          <span />
          {player?.autoplay ? 'On' : 'Off'}
        </button>
      </div>

      <div className="playlist-tool">
        <div className="playlist-intro">
          <span className="tool-icon secondary">
            <Icon name="import" size={22} />
          </span>
          <div className="tool-copy">
            <span className="tool-title-row">
              <b>Import a playlist or album</b>
            </span>
            <small>Paste a Spotify, Deezer or YouTube link and add it to the shared queue.</small>
          </div>
        </div>
        <form className="playlist-form" onSubmit={importCollection}>
          <input
            type="url"
            value={playlistUrl}
            onChange={(event) => setPlaylistUrl(event.target.value)}
            placeholder="Paste playlist or album link"
            aria-label="Playlist or album URL"
            disabled={importing}
          />
          <SourcePicker
            source={source}
            onChange={setSource}
            premium={premium}
            disabled={importing}
            compact
            mode="playlist"
          />
          <button
            className="import-button"
            disabled={!permissions?.canStart || !playlistUrl.trim() || importing}
          >
            <Icon name="import" size={16} />
            {importing ? 'Importing…' : 'Import'}
          </button>
        </form>
        {imported ? (
          <p className="import-success">{imported}</p>
        ) : (
          <p className="premium-note">
            Import up to {importLimit.toLocaleString('en-US')} tracks per link
            {!premium ? ' · Premium supports up to 1,000' : ''}.
          </p>
        )}
      </div>
    </section>
  )
}

function PremiumBadge() {
  return (
    <span className="premium-badge">
      <Icon name="lock" size={10} /> Premium
    </span>
  )
}

function BrandAvatar({ large = false }: { large?: boolean }) {
  return (
    <span className={`brand-avatar ${large ? 'large' : ''}`} aria-hidden="true">
      <img src={brandLogo} alt="" />
    </span>
  )
}

function SourcePicker({
  source,
  onChange,
  premium,
  disabled = false,
  compact = false,
  mode = 'search',
}: {
  source: PlayerSearchRequest['source']
  onChange(source: PlayerSearchRequest['source']): void
  premium: boolean
  disabled?: boolean
  compact?: boolean
  mode?: 'search' | 'playlist'
}) {
  const [open, setOpen] = useState(false)
  const pickerRef = useRef<HTMLDivElement>(null)
  const primarySource: PlayerSearchRequest['source'] = mode === 'playlist' ? 'spotify' : 'deezer'
  const sourceName = source === 'youtube' ? 'YouTube' : source === 'spotify' ? 'Spotify' : 'Deezer'
  const primaryName = primarySource === 'spotify' ? 'Spotify' : 'Deezer'
  const primaryDescription =
    primarySource === 'spotify' ? 'Playlist and album links' : 'Tracks, albums and links'
  const youtubeRequiresPremium = mode === 'search' && !premium

  useEffect(() => {
    if (!open) return
    const close = (event: PointerEvent) => {
      if (!pickerRef.current?.contains(event.target as Node)) setOpen(false)
    }
    const closeOnEscape = (event: KeyboardEvent) => event.key === 'Escape' && setOpen(false)
    document.addEventListener('pointerdown', close)
    document.addEventListener('keydown', closeOnEscape)
    return () => {
      document.removeEventListener('pointerdown', close)
      document.removeEventListener('keydown', closeOnEscape)
    }
  }, [open])

  return (
    <div className={`source-picker ${compact ? 'compact' : ''}`} ref={pickerRef}>
      <button
        type="button"
        className="source-trigger"
        aria-label="Music source"
        aria-haspopup="listbox"
        aria-expanded={open}
        disabled={disabled}
        onClick={() => setOpen((current) => !current)}
      >
        <SourceLogo source={source} />
        <span>
          <b>{sourceName}</b>
          {!compact ? (
            <small>
              {source === 'youtube'
                ? mode === 'playlist'
                  ? 'Playlist links'
                  : premium
                    ? 'Videos and playlists'
                    : 'Premium'
                : 'Music catalog'}
            </small>
          ) : null}
        </span>
        <Icon name="chevron" size={15} />
      </button>
      {open ? (
        <div className="source-menu" role="listbox" aria-label="Choose a music source">
          <button
            type="button"
            className={source === primarySource ? 'selected' : ''}
            role="option"
            aria-selected={source === primarySource}
            onClick={() => {
              onChange(primarySource)
              setOpen(false)
            }}
          >
            <SourceLogo source={primarySource} />
            <span>
              <b>{primaryName}</b>
              <small>{primaryDescription}</small>
            </span>
            <i className="source-check" />
          </button>
          {mode === 'playlist' ? (
            <button
              type="button"
              className={source === 'deezer' ? 'selected' : ''}
              role="option"
              aria-selected={source === 'deezer'}
              onClick={() => {
                onChange('deezer')
                setOpen(false)
              }}
            >
              <SourceLogo source="deezer" />
              <span>
                <b>Deezer</b>
                <small>Playlist and album links</small>
              </span>
              <i className="source-check" />
            </button>
          ) : null}
          <button
            type="button"
            className={source === 'youtube' ? 'selected' : ''}
            role="option"
            aria-selected={source === 'youtube'}
            disabled={youtubeRequiresPremium}
            title={youtubeRequiresPremium ? 'Best Music Boat Premium required' : undefined}
            onClick={() => {
              onChange('youtube')
              setOpen(false)
            }}
          >
            <SourceLogo source="youtube" />
            <span>
              <b>YouTube</b>
              <small>{mode === 'playlist' ? 'Playlist links' : 'Videos and playlists'}</small>
            </span>
            {youtubeRequiresPremium ? (
              <Icon name="lock" size={13} />
            ) : (
              <i className="source-check" />
            )}
          </button>
        </div>
      ) : null}
    </div>
  )
}

function SourceLogo({ source }: { source: PlayerSearchRequest['source'] }) {
  if (source === 'youtube')
    return (
      <span className="source-logo youtube" aria-hidden="true">
        <Icon name="play" size={11} />
      </span>
    )
  if (source === 'spotify')
    return (
      <span className="source-logo spotify" aria-hidden="true">
        <i />
        <i />
        <i />
      </span>
    )
  return (
    <span className="source-logo deezer" aria-hidden="true">
      <i />
      <i />
      <i />
      <i />
    </span>
  )
}

function SearchResult({
  track,
  disabled,
  onAction,
}: {
  track: SearchTrack
  disabled: boolean
  onAction(action: PlayerControlAction): Promise<boolean>
}) {
  return (
    <article className="result">
      <Artwork src={track.thumbnail} title={track.title} author={track.author} />
      <div>
        <b title={track.title}>{track.title}</b>
        <small>
          {track.author} · {formatDuration(track.duration, track.isStream)}
        </small>
      </div>
      <div className="result-actions">
        <button
          disabled={disabled}
          title="Play now"
          onClick={() =>
            void onAction({ type: 'addTrack', trackToken: track.trackToken, placement: 'now' })
          }
        >
          <Icon name="play" size={15} />
        </button>
        <button
          disabled={disabled}
          title="Add to queue"
          onClick={() =>
            void onAction({ type: 'addTrack', trackToken: track.trackToken, placement: 'end' })
          }
        >
          ＋ Queue
        </button>
      </div>
    </article>
  )
}

function Artwork({
  src,
  title,
  author,
  large = false,
}: {
  src: string | null
  title: string
  author?: string
  large?: boolean
}) {
  const primarySrc = artworkProxyUrl(src)
  const fallbackSrc = artworkFallbackUrl(title, author)
  const [imageSrc, setImageSrc] = useState<string | null>(primarySrc ?? fallbackSrc)
  useEffect(() => setImageSrc(primarySrc ?? fallbackSrc), [fallbackSrc, primarySrc])
  return (
    <div className={`artwork ${large ? 'large' : ''}`}>
      {imageSrc ? (
        <img
          src={imageSrc}
          alt={title ? `${title} artwork` : ''}
          onError={() =>
            setImageSrc((current) => (current !== fallbackSrc && fallbackSrc ? fallbackSrc : null))
          }
        />
      ) : (
        <span className="artwork-fallback">
          <Icon name="music" size={large ? 42 : 18} />
        </span>
      )}
    </div>
  )
}

function artworkProxyUrl(src: string | null): string | null {
  if (!src) return null
  if (src.startsWith('/') || src.startsWith('data:') || src.startsWith('blob:')) return src
  try {
    const url = new URL(src)
    if (url.protocol !== 'https:') return null
    return `/activity/media/artwork?url=${encodeURIComponent(url.toString())}&v=3`
  } catch {
    return null
  }
}

function artworkFallbackUrl(title: string, author?: string): string | null {
  const cleanedTitle = title.trim()
  if (!cleanedTitle || cleanedTitle === 'Best Music Boat' || cleanedTitle === 'Nothing playing') {
    return null
  }
  const params = new URLSearchParams({ title: cleanedTitle, v: '3' })
  if (author?.trim()) params.set('author', author.trim())
  return `/activity/media/artwork/fallback?${params.toString()}`
}

function Control({
  label,
  icon,
  disabled,
  onClick,
  primary = false,
  active = false,
  danger = false,
}: {
  label: string
  icon: ReactNode
  disabled: boolean
  onClick(): Promise<unknown>
  primary?: boolean
  active?: boolean
  danger?: boolean
}) {
  return (
    <button
      className={`control ${primary ? 'primary' : ''} ${active ? 'active' : ''} ${danger ? 'danger' : ''}`}
      aria-label={label}
      title={label}
      disabled={disabled}
      onClick={() => void onClick()}
    >
      {icon}
    </button>
  )
}

function LoadingScreen() {
  return (
    <main className="boot-screen">
      <div className="loader-note">
        <div className="loader-brand">
          <BrandAvatar large />
          <span>
            <b>Best Music Boat</b>
            <small>Discord Activity</small>
          </span>
        </div>
        <h1>Preparing your music room</h1>
        <p>Syncing your voice channel, queue, and player.</p>
        <div className="profile-progress" aria-label="Authenticating with Discord">
          <span />
        </div>
        <div className="loader-status">
          <i />
          Signing in securely with Discord
        </div>
      </div>
    </main>
  )
}
function ErrorScreen({ message }: { message: string }) {
  return (
    <main className="boot-screen">
      <div className="loader-note error-screen">
        <span className="error-mark">!</span>
        <h1>Couldn’t open the Activity</h1>
        <p>{message}</p>
        <small>Close and reopen Best Music Boat from your Discord voice channel.</small>
      </div>
    </main>
  )
}
function PlayerSkeleton() {
  return (
    <div className="player-skeleton">
      <i />
      <div>
        <i />
        <i />
        <i />
      </div>
    </div>
  )
}
function QueueSkeleton() {
  return (
    <div className="queue-skeleton">
      {[1, 2, 3, 4].map((item) => (
        <i key={item} />
      ))}
    </div>
  )
}
function SearchSkeleton() {
  return (
    <div className="result-skeleton">
      {[1, 2, 3].map((item) => (
        <i key={item} />
      ))}
    </div>
  )
}
